import { postQuotesQueryRequest, selectClient, selectMaybeClientPartner } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { calculateAge, zipCodeToState } from '@shared';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type PaymentOption = 'Single Pay' | '10-Pay' | 'Annually' | 'Monthly';

interface UseQuoteFetchingProps {
  open: boolean;
  selectedTab: ShopInsuranceTab;
  paymentOption: PaymentOption;
  hasPartnerWithDemographics: boolean;
  skipAutoFetch?: boolean;
}

export function useQuoteFetching({
  open,
  selectedTab,
  paymentOption,
  hasPartnerWithDemographics,
  skipAutoFetch = false,
}: UseQuoteFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const { flags } = useFeatureFlags();

  const fetchQuotesForPolicyType = (
    policyType: ShopInsuranceTab,
    cobPaymentOption: PaymentOption = paymentOption,
  ) => {
    if (!client || !client.intakeSurvey) {
      return;
    }

    const dateOfBirth = new Date(client.intakeSurvey.clientBirthDate);
    const age = calculateAge(dateOfBirth);

    const isTobacco = (user: Client) => {
      return (
        user.intakeSurvey.clientSmokerEver === 'Yes' ||
        user.intakeSurvey.clientSmokedRecently === 'Yes'
      );
    };

    const clientTobaccoStatus = isTobacco(client) ? 'Tobacco' : 'Non-Tobacco';
    const gender = client.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';
    const state = zipCodeToState(client.intakeSurvey.clientZipCode);
    const usaState =
      flags.nycLifeDemo && state.stateAbbreviation === 'NY' ? 'NJ' : state.stateAbbreviation;

    const policyTypeMapping = {
      single: 'Single Life',
      joint: 'Joint Life',
      couples: 'Couples Discount',
    };

    const demographicsData: FetchQuotesDemographics = {
      primaryAge: age,
      primaryGender: gender,
      primaryTobaccoStatus: clientTobaccoStatus,
      usaState,
      policyType: policyTypeMapping[policyType],
      cobPaymentOption,
      maritalStatus: client.intakeSurvey.clientMaritalStatus,
      predictedCareStartAge: client.appliedInferenceSet?.ltcAtAge,
    };

    if (policyType === 'joint' && hasPartnerWithDemographics && clientPartner?.intakeSurvey) {
      const partnerDateOfBirth = new Date(clientPartner.intakeSurvey.clientBirthDate);
      const partnerAge = calculateAge(partnerDateOfBirth);

      const partnerTobaccoStatus = isTobacco(clientPartner) ? 'Tobacco' : 'Non-Tobacco';
      const partnerGender =
        clientPartner.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';

      demographicsData.secondaryAge = partnerAge;
      demographicsData.secondaryGender = partnerGender;
      demographicsData.secondaryTobaccoStatus = partnerTobaccoStatus;
    }

    dispatch(
      postQuotesQueryRequest({ demographics: demographicsData, advisorId: client.advisorId }),
    );
  };

  useEffect(() => {
    if (!open || skipAutoFetch) {
      return;
    }

    fetchQuotesForPolicyType(selectedTab, paymentOption);
  }, [open, skipAutoFetch, client, clientPartner, dispatch, selectedTab, paymentOption]);

  return {
    fetchQuotesForPolicyType,
  };
}
